import { createContext, useState, useMemo } from "react";
import { LoanModel } from "./loans/loan.model";
import { useLoansQuery } from "modules/loan/queries/use-loans-query";

export interface LoansContextType {
  loans: LoanModel[];
  loadingLoans: boolean;
  filters: any;
  setFilters: (filters: any) => void;
  orderBy: any;
  setOrderBy: (orderBy: any) => void;
  refetch: () => void;
}

const LoansContext = createContext<LoansContextType>({
  loans: [],
  loadingLoans: false,
  filters: {},
  setFilters: () => {},
  orderBy: {},
  setOrderBy: () => {},
  refetch: () => {},
});

const LoansConsumer = LoansContext.Consumer;

function LoansProvider({ children }: { children: JSX.Element }) {
  const [filters, setFilters] = useState({});
  const [orderBy, setOrderBy] = useState({
    column: "created_at",
    direction: { ascending: false },
  });

  const { data: allLoans, isLoading: loadingLoans, refetch } = useLoansQuery();

  const loans = useMemo(() => {
    if (!allLoans) return [];

    let filteredLoans = [...allLoans];

    // Apply filters
    if (Object.keys(filters).length > 0) {
      filteredLoans = filteredLoans.filter(loan => {
        return Object.entries(filters).every(([key, value]) => {
          if (key === "status") {
            return loan.status.label === value;
          }
          return (loan as any)[key] === value;
        });
      });
    }

    // Apply sorting
    if (orderBy.column) {
      filteredLoans.sort((a, b) => {
        const aValue = (a as any)[orderBy.column];
        const bValue = (b as any)[orderBy.column];

        if (orderBy.direction.ascending) {
          return aValue > bValue ? 1 : -1;
        }
        return aValue < bValue ? 1 : -1;
      });
    }

    return filteredLoans;
  }, [allLoans, filters, orderBy]);

  return (
    <LoansContext.Provider
      value={{
        loans,
        filters,
        setFilters,
        loadingLoans,
        orderBy,
        setOrderBy,
        refetch: refetch,
      }}
    >
      {children}
    </LoansContext.Provider>
  );
}
export { LoansContext, LoansConsumer, LoansProvider };
